/**
 * @file binLayoutMode.ts
 * @description Defensive normalizer for `CanvasBin.layoutMode`.
 *
 * Existing projects (especially those authored on V2 before bin auto-layout
 * shipped) may have `layoutMode` stored as `undefined`, `null`, an empty
 * string, or a legacy/typo value. The plain `bin.layoutMode ?? 'freeform'`
 * fallback covers `null` / `undefined` but lets unexpected strings slip
 * through — they then fall through `computeBinLayout`'s `default` to grid
 * silently, while UI code looking for an exact match (`mode === 'freeform'`)
 * disagrees. Normalising at every read site keeps the rendering path and the
 * menu in sync regardless of stored value.
 */

export type BinLayoutMode = 'row' | 'grid' | 'columns' | 'freeform'

const VALID_LAYOUT_MODES: ReadonlyArray<BinLayoutMode> = ['freeform', 'grid', 'row', 'columns']

export function normalizeBinLayoutMode(mode: unknown): BinLayoutMode {
  return (VALID_LAYOUT_MODES as ReadonlyArray<string>).includes(mode as string) ? (mode as BinLayoutMode) : 'freeform'
}

interface ReviewWidthBin {
  layoutWidth?: number
  gridColumns?: number
  parentBinId?: string
}

/** New nested shot groups inherit the review track without per-run sizing writes.
 * An explicit manual grid resize opts that bin out of the inherited width. */
export function resolveBinLayoutWidth(
  bin: ReviewWidthBin,
  bins: ReadonlyMap<string, ReviewWidthBin>,
): number | undefined {
  let current: ReviewWidthBin | undefined = bin
  let depth = 0
  const visited = new Set<string>()
  while (current) {
    if (current.layoutWidth != null) return Math.max(200, current.layoutWidth - depth * 64)
    if (current.gridColumns != null || !current.parentBinId || visited.has(current.parentBinId)) return undefined
    visited.add(current.parentBinId)
    current = bins.get(current.parentBinId)
    depth++
  }
  return undefined
}
